import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiImage,
  FiRefreshCw,
  FiCalendar,
  FiX
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { bannersAPI } from '../services/api';
import { API_CONFIG } from '../config/api';
import ImageUpload from '../components/ImageUpload';

const Banners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  useEffect(() => {
    fetchBanners();
  }, []); // Only fetch once on component mount

  const fetchBanners = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from API first
      try {
        const response = await bannersAPI.getBanners(currentPage, pageSize, searchTerm);
        
        // Handle the actual backend response structure
        const data = response?.data || [];
        const pagination = response?.pagination || {};
        
        setBanners(Array.isArray(data) ? data : []);
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.totalItems || 0);
      } catch (apiError) {
        console.log('API endpoints not available, using mock data:', apiError);
        
        // Fallback to mock data
        const mockBanners = [
          {
            _id: '1',
            title: 'Personal Loan Banner',
            image: '/assets/images/banner1.jpg',
            description: 'Get quick personal loans with minimal documentation',
            link: '/personal-loan',
            isActive: true,
            sortOrder: 1,
            createdAt: new Date().toISOString()
          },
          {
            _id: '2',
            title: 'Home Loan Banner',
            image: '/assets/images/banner2.jpg',
            description: 'Buy your dream home with our competitive rates',
            link: '/home-loan',
            isActive: true,
            sortOrder: 2,
            createdAt: new Date(Date.now() - 86400000).toISOString()
          },
          {
            _id: '3',
            title: 'Business Loan Banner',
            image: '/assets/images/banner3.jpg',
            description: 'Expand your business with our flexible financing',
            link: '/business-loan',
            isActive: false,
            sortOrder: 3,
            createdAt: new Date(Date.now() - 172800000).toISOString()
          }
        ];
        
        setBanners(mockBanners);
        setTotalPages(1);
        setTotalItems(mockBanners.length);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast.error('Failed to load banners');
      setBanners([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await bannersAPI.deleteBanner(bannerId);
        toast.success('Banner deleted successfully');
        fetchBanners();
      } catch (error) {
        console.error('Error deleting banner:', error);
        toast.error('Failed to delete banner');
      }
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setShowModal(true);
  };

  // Helper function to get the full file URL
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    
    // If it's already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // If it's a data URL, return as is
    if (filePath.startsWith('data:')) {
      return filePath;
    }
    
    // Construct the full URL using the API base URL
    // Remove '/api' from the base URL since files are served from the root
    const baseUrl = API_CONFIG.ASSETS_URL;
    return `${baseUrl}/${filePath}`;
  };

  const handleSave = async (bannerData) => {
    try {
      if (editingBanner) {
        await bannersAPI.updateBanner(editingBanner._id, bannerData);
        toast.success('Banner updated successfully');
      } else {
        await bannersAPI.createBanner(bannerData);
        toast.success('Banner created successfully');
      }
      setShowModal(false);
      setEditingBanner(null);
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast.error('Failed to save banner');
    }
  };

  const exportToExcel = () => {
    const filteredData = banners.filter(banner => {
      const matchesText = banner.title?.toLowerCase().includes(filterText.toLowerCase()) ||
                         banner.description?.toLowerCase().includes(filterText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || banner.isActive === (filterStatus === 'active');
      return matchesText && matchesStatus;
    });

    const ws = XLSX.utils.json_to_sheet(filteredData.map(banner => ({
      Title: banner.title,
      Description: banner.description || 'N/A',
      Image: banner.image || 'N/A',
      Link: banner.link || 'N/A',
      'Sort Order': banner.sortOrder || 0,
      Status: banner.isActive ? 'Active' : 'Inactive',
      'Created At': new Date(banner.createdAt).toLocaleDateString()
    })));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Banners');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'banners.xlsx');
    toast.success('Banners exported to Excel');
  };

  const exportToPDF = () => {
    const filteredData = banners.filter(banner => {
      const matchesText = banner.title?.toLowerCase().includes(filterText.toLowerCase()) ||
                         banner.description?.toLowerCase().includes(filterText.toLowerCase());
      const matchesStatus = filterStatus === 'all' || banner.isActive === (filterStatus === 'active');
      return matchesText && matchesStatus;
    });

    const doc = new jsPDF();
    doc.autoTable({
      head: [['Title', 'Description', 'Image', 'Link', 'Sort Order', 'Status', 'Created At']],
      body: filteredData.map(banner => [
        banner.title,
        banner.description || 'N/A',
        banner.image || 'N/A',
        banner.link || 'N/A',
        banner.sortOrder || 0,
        banner.isActive ? 'Active' : 'Inactive',
        new Date(banner.createdAt).toLocaleDateString()
      ]),
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] }
    });
    doc.save('banners.pdf');
    toast.success('Banners exported to PDF');
  };

  const columns = [
    {
      name: 'Banner',
      selector: row => row.title,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-3">
          <div className="w-16 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center overflow-hidden">
            {row.image ? (
              <img src={getFileUrl(row.image)} alt={row.title} className="w-full h-full object-cover" />
            ) : (
              <FiImage className="w-6 h-6 text-white" />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">{row.title}</p>
            <p className="text-sm text-gray-500">{row.description}</p>
          </div>
        </div>
      ),
      width: '300px'
    },
    {
      name: 'Image',
      selector: row => row.image,
      sortable: false,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.image ? (
            <img src={getFileUrl(row.image)} alt={row.title} className="w-12 h-8 rounded object-cover" />
          ) : (
            <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center">
              <FiImage className="w-4 h-4 text-gray-500" />
            </div>
          )}
        </div>
      ),
      width: '100px'
    },
    {
      name: 'Link',
      selector: row => row.link,
      sortable: true,
      cell: row => (
        <span className="text-sm text-blue-600 hover:text-blue-800">
          {row.link || 'N/A'}
        </span>
      )
    },
    {
      name: 'Sort Order',
      selector: row => row.sortOrder,
      sortable: true,
      cell: row => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row.sortOrder || 0}
        </span>
      ),
      width: '100px'
    },
    {
      name: 'Status',
      selector: row => row.isActive,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          {row.isActive ? (
            <FiEye className="w-4 h-4 text-green-500" />
          ) : (
            <FiEyeOff className="w-4 h-4 text-red-500" />
          )}
          <span className={row.isActive ? 'text-green-600' : 'text-red-600'}>
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      name: 'Created',
      selector: row => row.createdAt,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-2">
          <FiCalendar className="w-4 h-4 text-gray-400" />
          <span>{new Date(row.createdAt).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      width: '100px'
    }
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: 'transparent'
      }
    },
    headRow: {
      style: {
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
        fontWeight: '600'
      }
    },
    rows: {
      style: {
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f8fafc'
        }
      }
    }
  };

  const filteredBanners = banners.filter(banner => {
    const matchesText = banner.title?.toLowerCase().includes(filterText.toLowerCase()) ||
                       banner.description?.toLowerCase().includes(filterText.toLowerCase());
    const matchesStatus = filterStatus === 'all' || banner.isActive === (filterStatus === 'active');
    return matchesText && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Banners Management</h1>
          <p className="text-gray-600 mt-1">Manage promotional banners and sliders</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Banner</span>
          </button>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search banners..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={fetchBanners}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={exportToExcel}
              className="flex items-center space-x-2 px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>Excel</span>
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center space-x-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <FiDownload className="w-4 h-4" />
              <span>PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          title=""
          columns={columns}
          data={filteredBanners}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50]}
          sortable
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          }
          noDataComponent={
            <div className="flex flex-col items-center justify-center p-8">
              <FiImage className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-500">No banners found</p>
            </div>
          }
        />
      </div>

      {/* Banner Modal */}
      <AnimatePresence>
        {showModal && (
          <BannerModal
            banner={editingBanner}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
              setEditingBanner(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Banner Modal Component
const BannerModal = ({ banner, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    title: banner?.title || '',
    description: banner?.description || '',
    image: banner?.image || '',
    link: banner?.link || '',
    sortOrder: banner?.sortOrder || 0,
    isActive: banner?.isActive ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {banner ? 'Edit Banner' : 'Add New Banner'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter banner description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image *</label>
              <ImageUpload
                currentImage={formData.image}
                onImageChange={(imageUrl) => setFormData({ ...formData, image: imageUrl })}
                accept="image/*"
                maxSize={5 * 1024 * 1024} // 5MB
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Link</label>
              <input
                type="text"
                value={formData.link}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Enter banner link..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="0"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                {banner ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Banners; 